// ✅ src/screens/EditProfileScreen.js (FULL)
// White background • Clean photo gallery upload • Add/Delete/Set Main photo
// ✅ NOW includes: username, interests, languages, lifestyle, prefAgeMin/max, relationshipGoal, education, job, height, etc.
// Firestore: datingProfiles/{uid}
// Storage: uploads to datingPhotos/{uid}/...

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../lib/firebase";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function lower(v) {
  return safe(v).toLowerCase();
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function toInputDate(ts) {
  const ms = msFromTs(ts);
  if (!ms) return "";
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function fromInputDate(str) {
  const s = safe(str);
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}
function calcAgeFromBirthDate(dateObj) {
  if (!dateObj) return null;
  const dob = dateObj instanceof Date ? dateObj : new Date(dateObj);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  if (age < 18 || age > 120) return null;
  return age;
}
function uniq(arr) {
  return Array.from(new Set((arr || []).map((x) => safe(x)).filter(Boolean)));
}
function uniqUrls(arr) {
  const out = [];
  const seen = new Set();
  for (const x of Array.isArray(arr) ? arr : []) {
    const u = safe(x);
    if (!u) continue;
    if (seen.has(u)) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
}
function titleCase(s) {
  const v = safe(s);
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
}
function slugifyUsername(v) {
  return safe(v)
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, 20);
}

/* ---------------- component ---------------- */
export default function EditProfileScreen() {
  const nav = useNavigate();

  const MAX_PHOTOS = 6;

  const [uid, setUid] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [msg, setMsg] = useState("");

  // form fields (core)
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("Ghana");
  const [gender, setGender] = useState(""); // male/female/other
  const [lookingFor, setLookingFor] = useState(""); // male/female/everyone
  const [relationshipGoal, setRelationshipGoal] = useState(""); // meetups etc
  const [about, setAbout] = useState("");
  const [birthDate, setBirthDate] = useState(""); // yyyy-mm-dd

  // extras
  const [height, setHeight] = useState(""); // string numeric
  const [education, setEducation] = useState("none");
  const [job, setJob] = useState("none");
  const [jobOther, setJobOther] = useState("");
  const [smoking, setSmoking] = useState("no");
  const [drinking, setDrinking] = useState("occasionally");
  const [religion, setReligion] = useState("none");

  // filters
  const [prefAgeMin, setPrefAgeMin] = useState("18"); // string
  const [prefAgeMax, setPrefAgeMax] = useState(""); // required ideally

  // tags
  const [languages, setLanguages] = useState(["English"]);
  const [interests, setInterests] = useState([]);

  // photos
  const [avatar, setAvatar] = useState(""); // main photo url
  const [photos, setPhotos] = useState([]); // array of urls
  const [newFiles, setNewFiles] = useState([]); // File[]
  const [uploading, setUploading] = useState(false);

  // custom add inputs
  const [customInterest, setCustomInterest] = useState("");
  const [customLanguage, setCustomLanguage] = useState("");

  const ageOptions = useMemo(() => {
    const out = [];
    for (let i = 18; i <= 80; i += 1) out.push(i);
    return out;
  }, []);

  const interestOptions = useMemo(
    () => [
      "music",
      "dancing",
      "movies",
      "football",
      "gym",
      "travel",
      "food",
      "cooking",
      "fashion",
      "gaming",
      "reading",
      "anime",
      "art",
      "photography",
      "beach",
      "hiking",
      "night outs",
      "church",
      "business",
      "tech",
      "comedy",
      "pets",
      "makeup",
      "netflix",
    ],
    []
  );

  const languageOptions = useMemo(
    () => ["English", "Twi", "Ga", "Ewe", "Hausa", "Fante", "French", "Other"],
    []
  );

  const educationOptions = useMemo(
    () => [
      { value: "none", label: "Prefer not to say" },
      { value: "high_school", label: "High school" },
      { value: "college", label: "College" },
      { value: "university", label: "University" },
      { value: "masters", label: "Masters" },
      { value: "phd", label: "PhD" },
      { value: "vocational", label: "Vocational/Technical" },
    ],
    []
  );

  const jobOptions = useMemo(
    () => [
      { value: "none", label: "Prefer not to say" },
      { value: "student", label: "Student" },
      { value: "self_employed", label: "Self-employed" },
      { value: "business_owner", label: "Business owner" },
      { value: "developer", label: "Software/IT" },
      { value: "healthcare", label: "Healthcare" },
      { value: "teacher", label: "Teacher" },
      { value: "finance", label: "Finance/Banking" },
      { value: "sales", label: "Sales/Marketing" },
      { value: "creative", label: "Creative/Media" },
      { value: "hospitality", label: "Hospitality" },
      { value: "public_service", label: "Public service" },
      { value: "trader", label: "Trader" },
      { value: "engineer", label: "Engineer" },
      { value: "other", label: "Other (type it)" },
    ],
    []
  );

  const relationshipGoalOptions = useMemo(
    () => [
      { value: "", label: "Select" },
      { value: "dating", label: "Dating" },
      { value: "serious", label: "Serious relationship" },
      { value: "friendship", label: "Friendship" },
      { value: "meetups", label: "Meetups" },
      { value: "marriage", label: "Marriage" },
      { value: "not_sure", label: "Not sure yet" },
    ],
    []
  );

  const smokingOptions = useMemo(
    () => [
      { value: "no", label: "No" },
      { value: "occasionally", label: "Occasionally" },
      { value: "yes", label: "Yes" },
    ],
    []
  );

  const drinkingOptions = useMemo(
    () => [
      { value: "no", label: "No" },
      { value: "occasionally", label: "Occasionally" },
      { value: "socially", label: "Socially" },
      { value: "yes", label: "Yes" },
    ],
    []
  );

  const religionOptions = useMemo(
    () => [
      { value: "none", label: "Prefer not to say" },
      { value: "christian", label: "Christian" },
      { value: "muslim", label: "Muslim" },
      { value: "traditional", label: "Traditional" },
      { value: "other", label: "Other" },
    ],
    []
  );

  const agePreview = useMemo(() => {
    const d = fromInputDate(birthDate);
    return calcAgeFromBirthDate(d);
  }, [birthDate]);

  // ✅ username uniqueness check (same as creation)
  async function isUsernameTaken(uname, myUid) {
    const u = slugifyUsername(uname);
    if (!u) return true;
    const qy = query(collection(db, "datingProfiles"), where("username", "==", u), limit(1));
    const snap = await getDocs(qy);
    if (snap.empty) return false;
    return snap.docs[0].id !== myUid;
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        nav("/login", { replace: true });
        return;
      }
      setUid(u.uid);
      setLoading(true);
      setMsg("");

      try {
        const snap = await getDoc(doc(db, "datingProfiles", u.uid));
        if (!snap.exists()) {
          nav("/create-profile", { replace: true });
          return;
        }

        const p = snap.data() || {};

        setDisplayName(safe(p.displayName));
        setUsername(safe(p.username));
        setCity(safe(p.city));
        setRegion(safe(p.region));
        setCountry(safe(p.country) || "Ghana");

        setGender(lower(p.gender));
        // ✅ IMPORTANT: normalize lookingFor to "everyone" (not "any")
        const lf = lower(p.lookingFor);
        setLookingFor(lf === "any" ? "everyone" : lf);

        setRelationshipGoal(lower(p.relationshipGoal));
        setAbout(safe(p.about || p.bio));

        // birthDate could be Timestamp, Date, or string YYYY-MM-DD
        if (p.birthDate?.toDate?.()) setBirthDate(new Date(p.birthDate.toDate()).toISOString().slice(0, 10));
        else if (p.birthDate instanceof Date) setBirthDate(p.birthDate.toISOString().slice(0, 10));
        else setBirthDate(safe(p.birthDate) || toInputDate(p.birthDate));

        // extras
        setHeight(p.height != null ? String(p.height) : "");
        setEducation(lower(p.education) || "none");
        setJob(lower(p.job) || "none");
        setJobOther(safe(p.jobOther));
        setSmoking(lower(p.smoking) || "no");
        setDrinking(lower(p.drinking) || "occasionally");
        setReligion(lower(p.religion) || "none");

        // prefs
        setPrefAgeMin(p.prefAgeMin != null ? String(p.prefAgeMin) : "18");
        setPrefAgeMax(p.prefAgeMax != null ? String(p.prefAgeMax) : "");

        // tags
        setLanguages(Array.isArray(p.languages) ? uniq(p.languages) : ["English"]);
        setInterests(Array.isArray(p.interests) ? uniq(p.interests).map((x) => lower(x)) : []);

        // photos
        const a = safe(p.avatar) || safe(p.photos?.[0]);
        const list = uniqUrls([a, ...(Array.isArray(p.photos) ? p.photos : [])]).slice(0, MAX_PHOTOS);
        setAvatar(a || list[0] || "");
        setPhotos(list);
      } catch (e) {
        console.error(e);
        setMsg("Could not load your profile. Please refresh.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [nav]);

  function setMainPhoto(url) {
    const u = safe(url);
    if (!u) return;
    setAvatar(u);
    setPhotos((prev) => {
      const cleaned = uniqUrls(prev);
      const without = cleaned.filter((x) => x !== u);
      return [u, ...without].slice(0, MAX_PHOTOS);
    });
  }

  function removePhoto(url) {
    const u = safe(url);
    if (!u) return;

    setPhotos((prev) => {
      const next = prev.filter((x) => x !== u);
      if (u === avatar) setAvatar(next[0] || "");
      return next;
    });
  }

  function movePhoto(url, dir) {
    const u = safe(url);
    if (!u) return;

    setPhotos((prev) => {
      const arr = [...prev];
      const i = arr.indexOf(u);
      if (i < 0) return arr;
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
      return arr;
    });
  }

  async function uploadSelectedFiles() {
    if (!uid) return;
    if (!newFiles.length) return;

    const remainingSlots = Math.max(0, MAX_PHOTOS - photos.length);
    const toUpload = newFiles.slice(0, remainingSlots);

    if (!toUpload.length) {
      setMsg(`You can upload up to ${MAX_PHOTOS} photos.`);
      return;
    }

    setUploading(true);
    setMsg("");

    try {
      const uploadedUrls = [];

      for (const file of toUpload) {
        const ext = safe(file.name).split(".").pop() || "jpg";
        const path = `datingPhotos/${uid}/photo_${Date.now()}_${Math.random().toString(16).slice(2)}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file, { contentType: file.type || "image/jpeg" });
        const url = await getDownloadURL(storageRef);
        uploadedUrls.push(url);
      }

      setPhotos((prev) => {
        const merged = uniqUrls([...prev, ...uploadedUrls]).slice(0, MAX_PHOTOS);
        if (!safe(avatar) && merged[0]) setAvatar(merged[0]);
        return merged;
      });

      setNewFiles([]);
    } catch (e) {
      console.error(e);
      setMsg("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  }

  function toggleInterest(name) {
    const item = lower(name);
    if (!item) return;
    setInterests((prev) => {
      const has = (prev || []).includes(item);
      const next = has ? (prev || []).filter((x) => x !== item) : uniq([...(prev || []), item]);
      return next.slice(0, 25);
    });
  }

  function addCustomInterest() {
    const item = lower(customInterest);
    if (!item) return;
    setInterests((prev) => uniq([...(prev || []), item]).slice(0, 25));
    setCustomInterest("");
  }

  function addLanguage(v) {
    const item = safe(v);
    if (!item) return;
    setLanguages((prev) => uniq([...(prev || []), item]).slice(0, 10));
  }

  function addCustomLanguage() {
    const item = safe(customLanguage);
    if (!item) return;
    addLanguage(item);
    setCustomLanguage("");
  }

  async function onSave(e) {
    e.preventDefault();
    if (saving) return;

    setMsg("");

    const dn = safe(displayName);
    const uname = slugifyUsername(username);
    const c = safe(city);
    const r = safe(region);
    const g = lower(gender);
    const lfRaw = lower(lookingFor);
    const lf = lfRaw === "any" ? "everyone" : lfRaw; // ✅ normalize
    const goal = lower(relationshipGoal);
    const ab = safe(about);

    const bd = fromInputDate(birthDate);
    const age = calcAgeFromBirthDate(bd);

    const minAgeNum = Number(prefAgeMin);
    const maxAgeNum = prefAgeMax === "" ? null : Number(prefAgeMax);

    const photoList = uniqUrls(photos).slice(0, MAX_PHOTOS);
    const main = safe(avatar) || photoList[0] || "";

    if (!dn) return setMsg("Please enter your display name.");
    if (!uname) return setMsg("Please choose a valid username.");
    if (!bd) return setMsg("Please select your birth date.");
    if (!age) return setMsg("You must be 18+ to use this dating app.");
    if (!c) return setMsg("Please enter your city.");
    if (!g) return setMsg("Please select your gender.");
    if (!lf) return setMsg("Please select who you’re looking for.");
    if (!main) return setMsg("Please add at least 1 photo.");

    if (!Number.isFinite(minAgeNum) || minAgeNum < 18) return setMsg("Preferred min age must be 18+.");
    if (maxAgeNum != null && (!Number.isFinite(maxAgeNum) || maxAgeNum < 18)) return setMsg("Preferred max age must be 18+.");
    if (maxAgeNum != null && minAgeNum > maxAgeNum) return setMsg("Min age cannot be higher than max age.");

    // username taken?
    try {
      setSaving(true);

      const taken = await isUsernameTaken(uname, uid);
      if (taken) {
        setMsg("That username is already taken. Try another one.");
        setSaving(false);
        return;
      }

      const finalJob = job === "other" ? safe(jobOther) : lower(job);

      const payload = {
        uid,
        displayName: dn,
        username: uname,

        city: c,
        region: r,
        country: safe(country) || "Ghana",

        gender: g,
        lookingFor: lf,

        relationshipGoal: goal,
        about: ab,

        birthDate: bd, // Date object -> Firestore Timestamp
        age,

        // extras
        height: safe(height) ? Number(safe(height)) : null,
        education: lower(education) || "none",
        job: lower(finalJob) || "none",
        jobOther: job === "other" ? safe(jobOther) : "",
        smoking: lower(smoking) || "no",
        drinking: lower(drinking) || "occasionally",
        religion: lower(religion) || "none",

        // prefs
        prefAgeMin: minAgeNum,
        prefAgeMax: maxAgeNum,

        // tags
        interests: uniq(interests).map((x) => lower(x)),
        languages: uniq(languages),

        // photos
        avatar: main,
        photos: photoList.includes(main) ? photoList : [main, ...photoList].slice(0, MAX_PHOTOS),

        profileComplete: true,
        updatedAt: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
      };

      await setDoc(doc(db, "datingProfiles", uid), payload, { merge: true });
      nav("/profile", { replace: true });
    } catch (err) {
      console.error(err);
      setMsg("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.center}>Loading your profile…</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        {/* Top */}
        <div style={styles.topRow}>
          <button type="button" style={styles.backBtn} onClick={() => nav(-1)}>
            ← Back
          </button>

          <div style={styles.titleWrap}>
            <div style={styles.title}>Edit Profile</div>
            <div style={styles.subtitle}>Update your info to get better matches.</div>
          </div>
        </div>

        <form onSubmit={onSave} style={styles.card}>
          {/* Photos */}
          <div style={styles.sectionTitle}>Photos</div>

          <div style={styles.photoPanel}>
            <div style={styles.photoHeader}>
              <div style={styles.photoHeaderLeft}>
                <div style={styles.photoLabel}>Your gallery</div>
                <div style={styles.photoHint}>
                  Add up to <b>{MAX_PHOTOS}</b> photos. Tap <b>Set Main</b> for your profile photo.
                </div>
              </div>

              <div style={styles.photoCountPill}>
                {photos.length}/{MAX_PHOTOS}
              </div>
            </div>

            {/* Gallery */}
            {photos.length ? (
              <div style={styles.gallery}>
                {photos.map((u, idx) => {
                  const isMain = safe(u) === safe(avatar);
                  return (
                    <div key={u} style={styles.photoCard}>
                      <div style={styles.photoImgWrap}>
                        <img src={u} alt="profile" style={styles.photoImg} />
                        {isMain ? <div style={styles.mainBadge}>Main</div> : null}
                      </div>

                      <div style={styles.photoActions}>
                        <button
                          type="button"
                          style={isMain ? styles.smallBtnDisabled : styles.smallBtn}
                          onClick={() => setMainPhoto(u)}
                          disabled={isMain}
                        >
                          Set Main
                        </button>

                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            style={idx === 0 ? styles.iconBtnDisabled : styles.iconBtn}
                            onClick={() => movePhoto(u, -1)}
                            disabled={idx === 0}
                            title="Move left"
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            style={idx === photos.length - 1 ? styles.iconBtnDisabled : styles.iconBtn}
                            onClick={() => movePhoto(u, +1)}
                            disabled={idx === photos.length - 1}
                            title="Move right"
                          >
                            →
                          </button>
                        </div>

                        <button type="button" style={styles.dangerBtn} onClick={() => removePhoto(u)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={styles.emptyPhotos}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>No photos yet</div>
                <div style={{ opacity: 0.75, fontWeight: 700 }}>
                  Add at least 1 photo so people can see you.
                </div>
              </div>
            )}

            {/* Upload row */}
            <div style={styles.uploadRow}>
              <label style={styles.uploadBox}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewFiles(files);
                  }}
                  style={{ display: "none" }}
                />
                <div style={styles.uploadIcon}>＋</div>
                <div>
                  <div style={styles.uploadTitle}>Choose photos</div>
                  <div style={styles.uploadSub}>
                    {newFiles.length ? `${newFiles.length} selected` : "JPG/PNG • Good lighting works best"}
                  </div>
                </div>
              </label>

              <button
                type="button"
                onClick={uploadSelectedFiles}
                disabled={uploading || !newFiles.length || photos.length >= MAX_PHOTOS}
                style={
                  uploading || !newFiles.length || photos.length >= MAX_PHOTOS
                    ? styles.primaryBtnDisabled
                    : styles.primaryBtn
                }
              >
                {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>

            {photos.length ? (
              <div style={styles.helper}>
                Tip: Put your best photo as <b>Main</b>. People swipe faster when the first photo is clear.
              </div>
            ) : null}
          </div>

          {/* Basics */}
          <div style={styles.sectionTitle}>Basics</div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Display name</label>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Ama" style={styles.input} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Username</label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. ama_queen"
                style={styles.input}
              />
              <div style={styles.helper}>Letters/numbers/underscore only (we auto-clean it on save).</div>
            </div>
          </div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Birth date</label>
              <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} style={styles.input} />
              <div style={styles.helper}>{agePreview ? `Age: ${agePreview}` : "Must be 18+."}</div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>City</label>
              <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Accra" style={styles.input} />
            </div>
          </div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Region</label>
              <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. Greater Accra" style={styles.input} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Country</label>
              <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Ghana" style={styles.input} />
            </div>
          </div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Gender</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)} style={styles.select}>
                <option value="">Select</option>
                <option value="male" style={{ color: "#111827" }}>Male</option>
                <option value="female" style={{ color: "#111827" }}>Female</option>
                <option value="other" style={{ color: "#111827" }}>Other</option>
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Looking for</label>
              <select value={lookingFor} onChange={(e) => setLookingFor(e.target.value)} style={styles.select}>
                <option value="">Select</option>
                <option value="male" style={{ color: "#111827" }}>Men</option>
                <option value="female" style={{ color: "#111827" }}>Women</option>
                <option value="everyone" style={{ color: "#111827" }}>Everyone</option>
              </select>
              <div style={styles.helper}>Note: your app uses <b>everyone</b> (not “any”).</div>
            </div>
          </div>

          {/* About */}
          <div style={styles.sectionTitle}>About</div>
          <div style={styles.field}>
            <label style={styles.label}>Bio</label>
            <textarea value={about} onChange={(e) => setAbout(e.target.value)} placeholder="A short bio 🙂" style={styles.textarea} rows={5} />
          </div>

          {/* Preferences */}
          <div style={styles.sectionTitle}>Preferences</div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Preferred ages from</label>
              <select value={prefAgeMin} onChange={(e) => setPrefAgeMin(e.target.value)} style={styles.select}>
                {ageOptions.map((n) => (
                  <option key={n} value={String(n)} style={{ color: "#111827" }}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Up to (max age)</label>
              <select value={prefAgeMax} onChange={(e) => setPrefAgeMax(e.target.value)} style={styles.select}>
                <option value="" style={{ color: "#111827" }}>No max</option>
                {ageOptions.map((n) => (
                  <option key={n} value={String(n)} style={{ color: "#111827" }}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Relationship goal</label>
            <select value={relationshipGoal} onChange={(e) => setRelationshipGoal(e.target.value)} style={styles.select}>
              {relationshipGoalOptions.map((x) => (
                <option key={x.value} value={x.value} style={{ color: "#111827" }}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>

          {/* Interests */}
          <div style={styles.sectionTitle}>Interests</div>
          <div style={styles.field}>
            <label style={styles.label}>Pick a few</label>
            <div style={styles.chipRow}>
              {interestOptions.map((x) => {
                const key = lower(x);
                const on = (interests || []).includes(key);
                return (
                  <div key={key} style={styles.chip(on)} onClick={() => toggleInterest(key)} role="button" tabIndex={0}>
                    {titleCase(x)}
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
              <input
                value={customInterest}
                onChange={(e) => setCustomInterest(e.target.value)}
                placeholder="Add custom interest (e.g. swimming)"
                style={{ ...styles.input, flex: 1, minWidth: 240 }}
              />
              <button type="button" style={styles.secondaryBtn} onClick={addCustomInterest}>
                Add
              </button>
            </div>
          </div>

          {/* Languages */}
          <div style={styles.sectionTitle}>Languages</div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Choose a language</label>
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) addLanguage(v);
                }}
                style={styles.select}
              >
                <option value="" style={{ color: "#111827" }}>Select</option>
                {languageOptions.map((x) => (
                  <option key={x} value={x} style={{ color: "#111827" }}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Or type your own</label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  value={customLanguage}
                  onChange={(e) => setCustomLanguage(e.target.value)}
                  placeholder="e.g. Dagbani"
                  style={{ ...styles.input, flex: 1, minWidth: 220 }}
                />
                <button type="button" style={styles.secondaryBtn} onClick={addCustomLanguage}>
                  Add
                </button>
              </div>
            </div>
          </div>

          {languages?.length ? (
            <div style={styles.pillRow}>
              {languages.map((x) => (
                <span key={x} style={styles.pill}>
                  {x}
                  <button
                    type="button"
                    style={styles.pillX}
                    onClick={() => setLanguages((prev) => (prev || []).filter((i) => i !== x))}
                    title="Remove"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          ) : null}

          {/* Lifestyle */}
          <div style={styles.sectionTitle}>Lifestyle</div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Drinking</label>
              <select value={drinking} onChange={(e) => setDrinking(e.target.value)} style={styles.select}>
                {drinkingOptions.map((x) => (
                  <option key={x.value} value={x.value} style={{ color: "#111827" }}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Smoking</label>
              <select value={smoking} onChange={(e) => setSmoking(e.target.value)} style={styles.select}>
                {smokingOptions.map((x) => (
                  <option key={x.value} value={x.value} style={{ color: "#111827" }}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Religion</label>
              <select value={religion} onChange={(e) => setReligion(e.target.value)} style={styles.select}>
                {religionOptions.map((x) => (
                  <option key={x.value} value={x.value} style={{ color: "#111827" }}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Education</label>
              <select value={education} onChange={(e) => setEducation(e.target.value)} style={styles.select}>
                {educationOptions.map((x) => (
                  <option key={x.value} value={x.value} style={{ color: "#111827" }}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>Job</label>
              <select value={job} onChange={(e) => setJob(e.target.value)} style={styles.select}>
                {jobOptions.map((x) => (
                  <option key={x.value} value={x.value} style={{ color: "#111827" }}>
                    {x.label}
                  </option>
                ))}
              </select>

              {job === "other" ? (
                <div style={{ marginTop: 10 }}>
                  <input
                    value={jobOther}
                    onChange={(e) => setJobOther(e.target.value)}
                    placeholder="Type your job…"
                    style={styles.input}
                  />
                </div>
              ) : null}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Height (cm)</label>
              <input
                value={height}
                onChange={(e) => setHeight(e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
                placeholder="e.g. 170"
                style={styles.input}
                inputMode="numeric"
              />
              <div style={styles.helper}>Optional.</div>
            </div>
          </div>

          {msg ? <div style={styles.msg}>{msg}</div> : null}

          <div style={styles.actions}>
            <button type="button" style={styles.secondaryBtn} onClick={() => nav("/profile")} disabled={saving}>
              Cancel
            </button>

            <button type="submit" style={saving ? styles.primaryBtnDisabled : styles.primaryBtn} disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- styles (WHITE, clean) ---------------- */
const styles = {
  page: {
    minHeight: "100vh",
    background: "#ffffff",
    padding: "18px 12px 40px",
    color: "#111827",
  },
  wrap: { width: "100%", maxWidth: 920, margin: "0 auto" },
  center: {
    minHeight: "70vh",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
  },

  topRow: { display: "flex", gap: 12, alignItems: "center", marginBottom: 14 },
  backBtn: {
    border: "1px solid rgba(0,0,0,0.10)",
    background: "#fff",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 900,
  },
  titleWrap: { minWidth: 0 },
  title: { fontSize: 20, fontWeight: 1000, letterSpacing: -0.4 },
  subtitle: { marginTop: 2, fontWeight: 700, opacity: 0.7, fontSize: 13 },

  card: {
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 18,
    padding: 14,
    boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
  },

  sectionTitle: {
    marginTop: 12,
    marginBottom: 10,
    fontWeight: 1000,
    fontSize: 14,
    opacity: 0.85,
  },

  /* Photo panel */
  photoPanel: {
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 16,
    padding: 12,
    background: "rgba(17,24,39,0.02)",
    marginBottom: 12,
  },
  photoHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  photoHeaderLeft: { minWidth: 0 },
  photoLabel: { fontWeight: 1000, fontSize: 14 },
  photoHint: { marginTop: 4, fontWeight: 700, opacity: 0.7, fontSize: 12 },
  photoCountPill: {
    borderRadius: 999,
    padding: "6px 10px",
    border: "1px solid rgba(0,0,0,0.10)",
    background: "#fff",
    fontWeight: 1000,
    fontSize: 12,
    flexShrink: 0,
  },

  gallery: {
    marginTop: 12,
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
  },
  photoCard: {
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#fff",
    overflow: "hidden",
  },
  photoImgWrap: { position: "relative", width: "100%", height: 210, background: "#e5e7eb" },
  photoImg: { width: "100%", height: "100%", objectFit: "cover" },
  mainBadge: {
    position: "absolute",
    left: 10,
    top: 10,
    background: "rgba(17,24,39,0.88)",
    color: "#fff",
    fontWeight: 1000,
    fontSize: 12,
    padding: "6px 10px",
    borderRadius: 999,
  },
  photoActions: {
    padding: 10,
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },

  smallBtn: {
    border: "1px solid rgba(0,0,0,0.12)",
    background: "#fff",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    fontSize: 13,
  },
  smallBtnDisabled: {
    border: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(0,0,0,0.03)",
    borderRadius: 999,
    padding: "10px 12px",
    fontWeight: 1000,
    fontSize: 13,
    opacity: 0.7,
    cursor: "not-allowed",
  },

  iconBtn: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 1000,
    fontSize: 14,
  },
  iconBtnDisabled: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(0,0,0,0.03)",
    fontWeight: 1000,
    fontSize: 14,
    opacity: 0.6,
    cursor: "not-allowed",
  },

  dangerBtn: {
    border: "1px solid rgba(239,68,68,0.28)",
    background: "rgba(239,68,68,0.08)",
    color: "#b91c1c",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    fontSize: 13,
  },

  emptyPhotos: {
    marginTop: 12,
    padding: 14,
    borderRadius: 16,
    border: "1px dashed rgba(0,0,0,0.18)",
    background: "#fff",
  },

  uploadRow: {
    marginTop: 12,
    display: "flex",
    gap: 10,
    alignItems: "stretch",
    flexWrap: "wrap",
  },
  uploadBox: {
    flex: 1,
    minWidth: 260,
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "#fff",
    cursor: "pointer",
  },
  uploadIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    background: "rgba(0,0,0,0.06)",
    fontSize: 22,
    fontWeight: 1000,
  },
  uploadTitle: { fontWeight: 1000 },
  uploadSub: { marginTop: 2, fontWeight: 700, opacity: 0.7, fontSize: 12 },

  /* Fields */
  field: { marginTop: 12 },
  label: { display: "block", fontWeight: 900, fontSize: 13, marginBottom: 6 },
  helper: { marginTop: 8, fontSize: 12, fontWeight: 700, opacity: 0.65 },

  input: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.12)",
    outline: "none",
    fontSize: 16,
    fontWeight: 700,
    background: "#fff",
  },
  select: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.12)",
    outline: "none",
    fontSize: 16,
    fontWeight: 800,
    background: "#fff",
    color: "#111827",
  },
  textarea: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.12)",
    outline: "none",
    fontSize: 16,
    fontWeight: 700,
    background: "#fff",
    resize: "vertical",
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
    marginTop: 0,
  },

  // chips + pills
  chipRow: { display: "flex", flexWrap: "wrap", gap: 10 },
  chip: (on) => ({
    padding: "10px 12px",
    borderRadius: 999,
    border: `1px solid ${on ? "rgba(239,68,68,0.30)" : "rgba(0,0,0,0.12)"}`,
    background: on ? "rgba(239,68,68,0.10)" : "#ffffff",
    color: "#111827",
    fontWeight: 900,
    cursor: "pointer",
    userSelect: "none",
  }),
  pillRow: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 999,
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.12)",
    fontSize: 12,
    fontWeight: 900,
    color: "#111827",
  },
  pillX: { border: "none", background: "transparent", cursor: "pointer", fontWeight: 950, color: "#ef4444" },

  msg: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(239,68,68,0.10)",
    color: "#b91c1c",
    fontWeight: 900,
  },

  actions: {
    marginTop: 14,
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  secondaryBtn: {
    border: "1px solid rgba(0,0,0,0.12)",
    background: "#fff",
    borderRadius: 999,
    padding: "12px 14px",
    cursor: "pointer",
    fontWeight: 1000,
  },
  primaryBtn: {
    border: "none",
    borderRadius: 999,
    padding: "12px 14px",
    cursor: "pointer",
    fontWeight: 1000,
    color: "#fff",
    background: "linear-gradient(135deg,#ef4444,#f97316)",
    boxShadow: "0 12px 26px rgba(239,68,68,0.18)",
  },
  primaryBtnDisabled: {
    border: "none",
    borderRadius: 999,
    padding: "12px 14px",
    fontWeight: 1000,
    color: "#fff",
    background: "rgba(239,68,68,0.55)",
    cursor: "not-allowed",
  },
};

/* ✅ Responsive: 2 columns on bigger screens */
if (typeof window !== "undefined") {
  const mq = window.matchMedia?.("(min-width: 720px)");
  if (mq?.matches) {
    styles.grid2.gridTemplateColumns = "1fr 1fr";
    styles.gallery.gridTemplateColumns = "1fr 1fr";
  }
}